import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Star, StarOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Modal } from '../../components/ui/modal';
import { useToast } from '../../contexts/ToastContext';
import { signatureTemplatesApi, type SignatureTemplate } from '../../services/signatureTemplatesApi';

export default function SignatureTemplatesPage() {
  const { success, error } = useToast();
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<SignatureTemplate | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['signature-templates'],
    queryFn: () => signatureTemplatesApi.list(true),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
      success('Plantilla eliminada');
    },
    onError: () => error('Error al eliminar'),
  });

  const toggleDefaultMutation = useMutation({
    mutationFn: ({ id, isDefault }: { id: string; isDefault: boolean }) =>
      signatureTemplatesApi.update(id, { isDefault }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Plantillas de Firma</h1>
          <p className="text-sm text-gray-500">Textos predefinidos para solicitudes de firma digital</p>
        </div>
        <Button size="sm" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Nueva plantilla
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400">Cargando...</div>
      ) : templates.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-gray-400">
          No hay plantillas de firma. Crea una para empezar.
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className={`rounded-lg border p-4 ${!tpl.isActive ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-sm">{tpl.name}</h3>
                    {tpl.isDefault && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                        Por defecto
                      </span>
                    )}
                    {tpl.allowDecline && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                        Permite rechazo
                      </span>
                    )}
                    {!tpl.isActive && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                        Inactiva
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{tpl.body}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => toggleDefaultMutation.mutate({ id: tpl.id, isDefault: !tpl.isDefault })}
                    className="p-1.5 hover:bg-gray-100 rounded"
                    title={tpl.isDefault ? 'Quitar por defecto' : 'Marcar por defecto'}
                  >
                    {tpl.isDefault ? (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    ) : (
                      <StarOff className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTemplate(tpl)}
                    className="p-1.5 hover:bg-gray-100 rounded"
                    title="Editar"
                  >
                    <Pencil className="h-4 w-4 text-gray-400" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('¿Eliminar esta plantilla?')) {
                        deleteMutation.mutate(tpl.id);
                      }
                    }}
                    className="p-1.5 hover:bg-gray-100 rounded"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {(isCreateOpen || editingTemplate) && (
        <TemplateFormModal
          template={editingTemplate}
          onClose={() => {
            setIsCreateOpen(false);
            setEditingTemplate(null);
          }}
          onSaved={() => {
            setIsCreateOpen(false);
            setEditingTemplate(null);
            queryClient.invalidateQueries({ queryKey: ['signature-templates'] });
          }}
        />
      )}
    </div>
  );
}

function TemplateFormModal({
  template,
  onClose,
  onSaved,
}: {
  template: SignatureTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { success, error } = useToast();
  const [name, setName] = useState(template?.name || '');
  const [body, setBody] = useState(template?.body || '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [allowDecline, setAllowDecline] = useState(template?.allowDecline ?? false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    try {
      if (template) {
        await signatureTemplatesApi.update(template.id, { name: name.trim(), body: body.trim(), isActive, allowDecline });
        success('Plantilla actualizada');
      } else {
        await signatureTemplatesApi.create({ name: name.trim(), body: body.trim(), allowDecline });
        success('Plantilla creada');
      }
      onSaved();
    } catch {
      error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={template ? 'Editar plantilla' : 'Nueva plantilla de firma'}
      size="md"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Ej: Recogida de vehículo"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texto para el cliente
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="Confirmo que recojo mi vehículo en buen estado..."
          />
          <p className="text-xs text-gray-400 mt-1">Este texto se muestra al cliente antes de firmar</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="allowDecline"
            checked={allowDecline}
            onChange={(e) => setAllowDecline(e.target.checked)}
          />
          <label htmlFor="allowDecline" className="text-sm text-gray-700">Permitir rechazo</label>
        </div>
        {template && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">Activa</label>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancelar</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !name.trim() || !body.trim()}>
            {saving ? 'Guardando...' : template ? 'Guardar' : 'Crear'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
